<!-- Reviews Table UI with search, filter, sort, and pagination -->
<app-page-breadcrumb [pageTitle]="'app.label.reviews' | translate">
  <div search class="w-64">
    <app-search-box
      [placeholder]="'app.review.search.placeholder' | translate"
      [ariaLabel]="'app.review.search.placeholder' | translate"
      [value]="searchText()"
      (valueChange)="onSearchTextChange($event)"
    />
  </div>
  <div actions class="relative" [matTooltip]="exportTooltip()">
    <app-button
      variant="outline"
      className="h-11 min-w-[11.5rem] whitespace-nowrap px-4 font-medium shadow-theme-xs"
      [startIcon]="reviewExportStore.inProgress() ? spinnerIcon : downloadIcon"
      (btnClick)="exportReviews()"
      [disabled]="reviewExportStore.inProgress() || !!reviewsStore.searchText()"
    >
      @if (reviewExportStore.inProgress()) {
        {{ 'app.review.export.inProgress' | translate }}
        @if (reviewExportStore.percent() !== undefined) {
          <span class="tabular-nums text-gray-500">{{ reviewExportStore.percent() }}%</span>
        }
      } @else {
        {{ 'app.review.export.download' | translate }}
      }
    </app-button>
    @if (reviewExportStore.inProgress()) {
      <div class="absolute inset-x-3 -bottom-1.5 overflow-hidden rounded-full" data-testid="export-progress">
        <mat-progress-bar
          [mode]="reviewExportStore.percent() !== undefined ? 'determinate' : 'indeterminate'"
          [value]="reviewExportStore.percent() ?? 0"
        ></mat-progress-bar>
      </div>
    }
  </div>
  <div actions>
    <app-button
      variant="outline"
      className="h-11 whitespace-nowrap px-4 font-medium shadow-theme-xs"
      [startIcon]="historyIcon"
      data-testid="export-history"
      (btnClick)="openExportHistory()"
    >
      {{ 'app.review.export.history.button' | translate }}
    </app-button>
  </div>
</app-page-breadcrumb>

<app-component-card>
  <div appStickyOffset class="overflow-visible w-full">
    <table class="reviews"
           mat-table
           [dataSource]="dataSource"
           matSort
           [matSortActive]="sortActive"
           [matSortDirection]="sortDirection"
           matSortStart="desc"
           (matSortChange)="sortData($event)">

      <!-- Date: posted + edited -->
      <ng-container matColumnDef="posted">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="commentedAt">{{ 'app.label.posted' | translate }}</th>
        <td mat-cell *matCellDef="let r" class="date-cell">
          <span class="primary">{{ r.commentedAt | date: 'mediumDate' }}</span>
          @if (isEdited(r.commentedAt, r.updatedAt)) {
            <span class="meta" [matTooltip]="(r.updatedAt | date: 'medium') ?? ''">
              <mat-icon inline>edit</mat-icon>{{ 'app.label.edited' | translate }} {{ r.updatedAt | date: 'mediumDate' }}
            </span>
          }
        </td>
      </ng-container>

      <!-- Customer: author + dealer -->
      <ng-container matColumnDef="customer">
        <th mat-header-cell *matHeaderCellDef>{{ 'app.label.customer' | translate }}</th>
        <td mat-cell *matCellDef="let r">
          <span class="primary">{{ r.authorName }}</span>
          <span class="meta">{{ r.dealerName }}</span>
        </td>
      </ng-container>

      <!-- Review: rating + text -->
      <ng-container matColumnDef="review">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="rating">{{ "app.review.review" | translate }}</th>
        <td mat-cell *matCellDef="let r" class="text-cell">
      <span class="stars" [attr.aria-label]="r.rating + ' out of 5 stars'"
            >
        @for (s of stars; track s; let i = $index) {
          @let filled = i < r.rating;
          <mat-icon inline
                    [class.text-gray-300]="!filled"
                    [class.text-red-600]="filled && r.rating <= 2"
                    [class.text-yellow-600]="filled && (r.rating === 3 || r.rating === 4)"
                    [class.text-green-600]="filled && r.rating === 5">{{ s <= r.rating ? 'star' : 'star' }}</mat-icon>
        }
      </span>
          <p class="clamp" [matTooltip]="r.response" matTooltipClass="wide-tooltip">{{ r.response }}</p>
        </td>
      </ng-container>

      <!-- Response: text + responded/edited dates, or Reply action -->
      <ng-container matColumnDef="response">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="respondedAt">{{ "app.label.your.response" | translate }}</th>
        <td mat-cell *matCellDef="let r" class="text-cell">
          @if (r.comment) {
            <p class="clamp" [matTooltip]="r.comment" matTooltipClass="wide-tooltip">{{ r.comment }}</p>
            <span class="meta">
              <mat-icon inline class="ok">check_circle</mat-icon>
              {{ 'app.label.replied' | translate }} {{ r.respondedAt | date: 'mediumDate' }}
              @if (isEdited(r.respondedAt, r.responseUpdatedAt)) {
                <span [matTooltip]="(r.responseUpdatedAt | date: 'medium') ?? ''">
              · {{ 'app.label.edited' | translate }} {{ r.responseUpdatedAt | date: 'mediumDate' }}
            </span>
            }
        </span>
          } @else {
            <app-button size="sm" variant="primary" (btnClick)="openReplyForm(r)">
              <mat-icon>reply</mat-icon>
              {{ "app.review.reply" | translate }}
            </app-button>
          }
        </td>
      </ng-container>

      <!-- Tags -->
      <ng-container matColumnDef="tags">
        <th mat-header-cell *matHeaderCellDef>Tags</th>
        <td mat-cell *matCellDef="let r">
          @if (r.labels.length) {
            <mat-chip-set>
              @for (t of r.labels; track t) { <mat-chip>{{ t.name }}</mat-chip> }
            </mat-chip-set>
          } @else {
            <button matButton (click)="openTagsForm(r)"><mat-icon inline class="ok">add</mat-icon><span>Add tag</span></button>
          }
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let r; columns: displayedColumns" [class.awaiting]="!r.comment"></tr>
    </table>
  </div>
</app-component-card>
@if (reviewsStore.loading()) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}
@if (pageToken) {
  <div class="flex justify-center w-full mt-4">
    <app-button variant="primary" (btnClick)="fetchReviews(true)" [disabled]="reviewsStore.loading()">
      {{ "app.review.next.page" | translate }}
    </app-button>
  </div>
}
